import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { Abonnement } from '../types/abonnement';
import { formatXOF } from '../utils/feeCalculator';
import { Currency } from '../types/calculator';
import { AbonnementDetailModal } from './AbonnementDetailModal';
interface PaymentCalendarProps {
  abonnements: Abonnement[];
}
interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  payments: Abonnement[];
}
// Color palette for subscriptions
const SUBSCRIPTION_COLORS = [{
  bg: 'bg-blue-100',
  border: 'border-blue-300',
  text: 'text-blue-900',
  hover: 'hover:bg-blue-200'
}, {
  bg: 'bg-purple-100',
  border: 'border-purple-300',
  text: 'text-purple-900',
  hover: 'hover:bg-purple-200'
}, {
  bg: 'bg-pink-100',
  border: 'border-pink-300',
  text: 'text-pink-900',
  hover: 'hover:bg-pink-200'
}, {
  bg: 'bg-rose-100',
  border: 'border-rose-300',
  text: 'text-rose-900',
  hover: 'hover:bg-rose-200'
}, {
  bg: 'bg-orange-100',
  border: 'border-orange-300',
  text: 'text-orange-900',
  hover: 'hover:bg-orange-200'
}, {
  bg: 'bg-amber-100',
  border: 'border-amber-300',
  text: 'text-amber-900',
  hover: 'hover:bg-amber-200'
}, {
  bg: 'bg-lime-100',
  border: 'border-lime-300',
  text: 'text-lime-900',
  hover: 'hover:bg-lime-200'
}, {
  bg: 'bg-emerald-100',
  border: 'border-emerald-300',
  text: 'text-emerald-900',
  hover: 'hover:bg-emerald-200'
}, {
  bg: 'bg-teal-100',
  border: 'border-teal-300',
  text: 'text-teal-900',
  hover: 'hover:bg-teal-200'
}, {
  bg: 'bg-cyan-100',
  border: 'border-cyan-300',
  text: 'text-cyan-900',
  hover: 'hover:bg-cyan-200'
}, {
  bg: 'bg-sky-100',
  border: 'border-sky-300',
  text: 'text-sky-900',
  hover: 'hover:bg-sky-200'
}, {
  bg: 'bg-indigo-100',
  border: 'border-indigo-300',
  text: 'text-indigo-900',
  hover: 'hover:bg-indigo-200'
}, {
  bg: 'bg-violet-100',
  border: 'border-violet-300',
  text: 'text-violet-900',
  hover: 'hover:bg-violet-200'
}, {
  bg: 'bg-fuchsia-100',
  border: 'border-fuchsia-300',
  text: 'text-fuchsia-900',
  hover: 'hover:bg-fuchsia-200'
}];
export function PaymentCalendar({
  abonnements
}: PaymentCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedAbonnement, setSelectedAbonnement] = useState<Abonnement | null>(null);
  // Create a consistent color mapping for each unique subscription name
  const getColorForSubscription = (name: string): (typeof SUBSCRIPTION_COLORS)[0] => {
    // Simple hash function to get consistent color for same name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % SUBSCRIPTION_COLORS.length;
    return SUBSCRIPTION_COLORS[index];
  };
  const formatAmount = (amount: number, currency: Currency) => {
    if (currency === 'XOF') {
      return formatXOF(amount);
    } else if (currency === 'EUR') {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR'
      }).format(amount);
    } else {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    }
  };
  const getDaysInMonth = (date: Date): CalendarDay[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    // First day of the month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    // Get day of week (0 = Sunday, 1 = Monday, etc.)
    const firstDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const days: CalendarDay[] = [];
    // Add days from previous month
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    const startDay = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Start on Monday
    for (let i = startDay; i > 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay - i + 1);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: false,
        payments: getPaymentsForDate(date)
      });
    }
    // Add days of current month
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push({
        date,
        isCurrentMonth: true,
        isToday: date.getTime() === today.getTime(),
        payments: getPaymentsForDate(date)
      });
    }
    // Add days from next month to complete the grid
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: false,
        payments: getPaymentsForDate(date)
      });
    }
    return days;
  };
  const getPaymentsForDate = (date: Date): Abonnement[] => {
    const dateStr = date.toISOString().split('T')[0];
    return abonnements.filter(a => a.next_payment_date === dateStr);
  };
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };
  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  const handlePaymentClick = (payment: Abonnement) => {
    setSelectedAbonnement(payment);
  };
  const monthName = currentDate.toLocaleDateString('fr-FR', {
    month: 'long',
    year: 'numeric'
  });
  const days = getDaysInMonth(currentDate);
  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  return <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-249">
        {/* Calendar Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-4" data-id="element-250">
          <div className="flex items-center justify-between" data-id="element-251">
            <h2 className="text-xl font-bold text-gray-900 capitalize" data-id="element-252">
              {monthName}
            </h2>
            <div className="flex items-center gap-2" data-id="element-253">
              <button onClick={goToToday} className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-254">
                Aujourd'hui
              </button>
              <div className="flex items-center gap-1" data-id="element-255">
                <button onClick={goToPreviousMonth} className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-256">
                  <ChevronLeft className="w-5 h-5" data-id="element-257" />
                </button>
                <button onClick={goToNextMonth} className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors" data-id="element-258">
                  <ChevronRight className="w-5 h-5" data-id="element-259" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="p-4" data-id="element-260">
          {/* Week days header */}
          <div className="grid grid-cols-7 gap-2 mb-2" data-id="element-261">
            {weekDays.map(day => <div key={day} className="text-center text-xs font-semibold text-gray-600 py-2" data-id="element-262">
                {day}
              </div>)}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-2" data-id="element-263">
            {days.map((day, index) => {
            const hasPayments = day.payments.length > 0;
            const isUrgent = day.payments.some(p => {
              const daysUntil = Math.ceil((new Date(p.next_payment_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
              return daysUntil <= 7 && daysUntil >= 0;
            });
            return <motion.div key={index} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: index * 0.005
            }} className={`
                    min-h-[100px] p-2 rounded-lg border transition-all
                    ${day.isCurrentMonth ? 'bg-white' : 'bg-gray-50'}
                    ${day.isToday ? 'border-blue-500 border-2 bg-blue-50' : 'border-gray-200'}
                  `} data-id="element-264">
                  <div className={`
                    text-sm font-medium mb-1
                    ${day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}
                    ${day.isToday ? 'text-blue-700 font-bold' : ''}
                  `} data-id="element-265">
                    {day.date.getDate()}
                  </div>

                  {/* Payments for this day */}
                  <div className="space-y-1" data-id="element-266">
                    {day.payments.map(payment => {
                  const colors = getColorForSubscription(payment.name);
                  const daysUntil = Math.ceil((new Date(payment.next_payment_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                  const isPaymentUrgent = daysUntil <= 7 && daysUntil >= 0;
                  const isPaymentPast = daysUntil < 0;
                  return <button key={payment.id} onClick={() => handlePaymentClick(payment)} className={`
                            w-full text-left text-xs p-2 rounded-lg border-2 transition-all cursor-pointer
                            ${colors.bg} ${colors.border} ${colors.text} ${colors.hover}
                            hover:shadow-md hover:scale-105 transform
                            ${isPaymentUrgent ? 'ring-2 ring-orange-400 ring-offset-1' : ''}
                            ${isPaymentPast ? 'ring-2 ring-red-400 ring-offset-1 opacity-75' : ''}
                          `} data-id="element-267">
                          <div className="font-bold truncate mb-0.5" data-id="element-268">
                            {payment.name}
                          </div>
                          <div className="font-semibold truncate" data-id="element-269">
                            {formatAmount(payment.amount, payment.currency as Currency)}
                          </div>
                          {isPaymentUrgent && <div className="text-[10px] font-bold text-orange-700 mt-0.5" data-id="element-270">
                              ⚠️ {daysUntil}j
                            </div>}
                          {isPaymentPast && <div className="text-[10px] font-bold text-red-700 mt-0.5" data-id="element-271">
                              🔴 Retard
                            </div>}
                        </button>;
                })}
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Legend */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-3" data-id="element-272">
          <div className="flex items-center gap-6 text-xs text-gray-600" data-id="element-273">
            <div className="flex items-center gap-2" data-id="element-274">
              <div className="w-4 h-4 rounded border-2 border-blue-500 bg-blue-50" data-id="element-275"></div>
              <span data-id="element-276">Aujourd'hui</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-277">
              <div className="w-4 h-4 rounded border-2 border-orange-400 ring-2 ring-orange-400 ring-offset-1" data-id="element-278"></div>
              <span data-id="element-279">Paiement urgent (≤7 jours)</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-280">
              <div className="w-4 h-4 rounded border-2 border-red-400 ring-2 ring-red-400 ring-offset-1 opacity-75" data-id="element-281"></div>
              <span data-id="element-282">Paiement en retard</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-283">
              <div className="flex gap-1" data-id="element-284">
                <div className="w-3 h-3 rounded bg-blue-100 border border-blue-300" data-id="element-285"></div>
                <div className="w-3 h-3 rounded bg-purple-100 border border-purple-300" data-id="element-286"></div>
                <div className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300" data-id="element-287"></div>
              </div>
              <span data-id="element-288">Couleurs par abonnement</span>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedAbonnement && <AbonnementDetailModal abonnement={selectedAbonnement} onClose={() => setSelectedAbonnement(null)} data-id="element-289" />}
    </>;
}